import { h, ref } from 'vue';
import i18n from '@/i18n';
import {
  EXPIRED,
  NEW,
  VOID,
  IN_PROGRESS,
  QUOTE_READY,
  RQ_TO_BOOK,
  BOOKED,
  DEPOSIT_DUE,
  COMPLETE_NAMES,
  FINAL_PAYMENT_DUE,
  BOOKING_COMPLETED,
  QT_COORDINATOR,
  QT_COMMERCIAL,
  QT_REVENUE,
  QT_AIR,
  QT_CRUISE,
  QT_INCENTIVE,
  QT_SUPERVISOR,
  PACKAGE_LAND_ONLY,
  LAND_ONLY_PACKAGE,
  PACKAGE,
  LAND_ONLY,
  CRUISE,
  AIR_ONLY,
  NONE,
  BOOKING_CANCELLED,
  PRICE_MATCH,
  LANG_ENGLISH,
  AFR,
  ASI,
  CRU,
  EUR,
  MID,
  NAM,
  SOM,
  SOP,
  SUN,
  HOTEL_MANAGEMENT,
  PACKAGE_MANAGEMENT,
  AIR_MANAGEMENT,
  TOUR_MANAGEMENT,
  CRUISE_MANAGEMENT,
  TERM_AND_CONDITIONS,
  HOTEL_CONFIGURATION,
  PACKAGE_CONFIGURATION,
  DESTINATION_MANAGEMENT,
  GATEWAY_MANAGEMENT,
  FLIGHT_RULE_MANAGEMENT,
  DESTINATION_GROUP_MANAGEMENT,
  GATE_DEST_DURATION_MANAGEMENT,
  TOUR_COMBINATION,
  TOUR_DETAIL,
  CITY,
  CRUISE_DESTINATION,
  CRUISE_LINES,
  CRUISE_SHIPS,
  CRUISE_SAILINGS,
  CUSTOMER_QUOTE_FORM,
  PDF,
  QUOTE_TO_BOOK_TERM,
  HOTEL_ONLY,
  TOUR,
  INCENTIVES,
  PROMOS,
  HOME_PAGE,
  TOP_BANNER,
  SIDE_PROMOS,
  PROMO_BOX,
  FORM_PROMOS,
  SURFACE_PROMO,
  ACCORDION_PROMOS,
  QUOTE_CONFIGURATION,
  RULE_ASSIGNMENT,
  ADMIN_SETUP,
  CONTENT_MANAGEMENT_UPDATE,
  DATA_SYNC,
  AIRLINE_MANAGEMENT,
  LOGO,
  AIRLINE_TAX,
  PROMO_TITLE_CONFIG,
  GROUP_TYPE_MANAGEMENT,
  ROLE,
  TRAVEL_HOME,
  MY_QUOTE,
  QUOTE_MANAGEMENT,
  ADMINISTRATION,
  REPORT,
  EMAIL_CONFIGURATION,
  EXPIRY_CONFIGURATION,
  OPEN_JAW_CONFIGURATION,
  CALENDAR_CONFIGURATION,
  MARGIN_SAFETY,
  FTV_CONFIGURATION,
  URL,
  EMAIL,
  FBR,
  EMAIL_DEPOSIT_DUE,
  EMAIL_FINAL_PAYMENT_DUE,
  EMAIL_PASSENGER_LIST_DUE,
  EMAIL_QUOTE_EXPIRING,
  HOTEL,
  AIR,
  MDT,
  FULL_RISK,
  SHARED_RISK,
  PRE_REG,
  MAX_ROOM_CONFIGURATION,
  OPTION_CONFIGURATION,
  AGENCY_BLACKLIST,
  MAX_ROOM,
  GROUP_SIZE
} from '@/constants';
import { formatDate, resetTimer } from '@/utils/dateUtils';
import { DateTime } from 'luxon';
import { useDialog } from 'primevue/usedialog';
import routerKey from '@/router/routerKey';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import Button from 'primevue/button';
import ErrorMessageModal from '@/components/modal/ErrorMessageModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

export function useViewComposable() {
  const quoteSts = ref([
    { label: i18n.global.t('quote_status.received').toUpperCase(), value: NEW, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.in_progress').toUpperCase(), value: IN_PROGRESS, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.quote_ready').toUpperCase(), value: QUOTE_READY, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.price_match').toUpperCase(), value: PRICE_MATCH, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.rq_to_book').toUpperCase(), value: RQ_TO_BOOK, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.booked').toUpperCase(), value: BOOKED, before_book: false, disabled: false },
    { label: i18n.global.t('quote_status.deposit_due').toUpperCase(), value: DEPOSIT_DUE, before_book: false, disabled: false },
    { label: i18n.global.t('quote_status.complete_names').toUpperCase(), value: COMPLETE_NAMES, before_book: false, disabled: false },
    { label: i18n.global.t('quote_status.final_payment_due').toUpperCase(), value: FINAL_PAYMENT_DUE, before_book: false, disabled: false },
    { label: i18n.global.t('quote_status.booking_completed').toUpperCase(), value: BOOKING_COMPLETED, before_book: false, disabled: false },
    { label: i18n.global.t('quote_status.expired').toUpperCase(), value: EXPIRED, before_book: true, disabled: true },
    { label: i18n.global.t('quote_status.void').toUpperCase(), value: VOID, before_book: true, disabled: false },
    { label: i18n.global.t('quote_status.booking_cancelled').toUpperCase(), value: BOOKING_CANCELLED, before_book: false, disabled: false }
  ]);

  const apiOrder = ref([
    { label: i18n.global.t('management.hotel.package_land_only').toUpperCase(), value: PACKAGE_LAND_ONLY },
    { label: i18n.global.t('management.hotel.land_only_package').toUpperCase(), value: LAND_ONLY_PACKAGE },
    { label: i18n.global.t('management.hotel.package').toUpperCase(), value: PACKAGE },
    { label: i18n.global.t('management.hotel.land_only').toUpperCase(), value: LAND_ONLY },
    { label: i18n.global.t('management.hotel.none').toUpperCase(), value: NONE }
  ]);
  const priorityStatus = new Map([
    [NEW, '1'],
    [VOID, '1'],
    [BOOKING_CANCELLED, '2'],
    [IN_PROGRESS, '2'],
    [QUOTE_READY, '3'],
    [RQ_TO_BOOK, '4'],
    [PRICE_MATCH, '4'],
    [BOOKED, '5'],
    [DEPOSIT_DUE, '6'],
    [COMPLETE_NAMES, '7'],
    [FINAL_PAYMENT_DUE, '8'],
    [BOOKING_COMPLETED, '9'],
    [EXPIRED, '0']
  ]);
  const statusPriorityNumber = {
    ZERO: 0,
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
    FIVE: 5,
    SIX: 6,
    SEVEN: 7,
    EIGHT: 8,
    NINE: 9
  };
  const departments = ref([
    { label: 'CO - COORDINATOR', standsFor: 'CO', value: QT_COORDINATOR },
    { label: 'GC - COMMERCIAL', standsFor: 'GC', value: QT_COMMERCIAL },
    { label: 'RM - REVENUE', standsFor: 'RM', value: QT_REVENUE },
    { label: 'AO - AIR', standsFor: 'AO', value: QT_AIR },
    { label: 'CR - CRUISE', standsFor: 'CR', value: QT_CRUISE },
    { label: 'IN - INCENTIVES', standsFor: 'IN', value: QT_INCENTIVE },
    { label: 'SU - SUPERVISOR', standsFor: 'SU', value: QT_SUPERVISOR }
  ]);

  const renderLocalTimeFromUtc = (date) => {
    const dateTime = new Date(date);
    return new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000);
  };

  const renderAmount = (amount) => {
    return `$${amount ? Number(amount).toFixed(2) : '0.00'}`;
  };
  const renderDateTimeOption = (dates) => {
    if (dates && typeof dates === 'string') {
      return dates
        ?.split(',')
        .map((date) => formatDate(date, 'dd-LLL-yyyy', true, i18n.global.locale.value))
        .join('\n');
    }
    return '';
  };

  const renderAmountAccordingRouter = (amount, routerName) => {
    if (routerName === routerKey.MY_QUOTE) {
      return amount ? `$${Number(amount).toFixed(2)}` : 'N/A';
    } else {
      return renderAmount(amount);
    }
  };

  const createUnassignedStatus = () => {
    return {
      members: [
        {
          email: '',
          hide: false,
          label: 'UNASSIGNED'
        }
      ]
    };
  };

  const getDepartment = (department, users) => {
    const usersByDept = users.reduce((result, user) => {
      if (department === user?.displayName) {
        if (user.members && Array.isArray(user.members)) {
          result = {
            ...user,
            members: user.members
              .map((member) => ({
                label: `${member.givenName} ${member.surname}`,
                email: member.mail,
                hide: false
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          };
        }
      }
      return result;
    }, null);

    return usersByDept || {};
  };

  const removeHtmlTags = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent ?? div.innerText ?? '';
    return text.trim();
  };

  const renderTitleBooking = (quoteValue) => {
    if (quoteValue?.qType === CRUISE) {
      return i18n.global.t('booking.sub_title_one_cruise');
    } else if (quoteValue?.qType === AIR_ONLY) {
      return i18n.global.t('booking.sub_title_one_air');
    } else {
      return i18n.global.t('booking.sub_title_one_package');
    }
  };

  const styleColumnExport = (columns) => {
    return columns.map((column) => ({
      wch: column
    }));
  };

  const renderNameFileExport = (prev) => {
    const dt = DateTime.local();
    const formattedDate = dt.toFormat('yyyyLLLdd');
    return `${prev}_${formattedDate}.xlsx`;
  };

  const formatRoomExclusionData = (listRoom: object[]) => {
    return listRoom?.map((category: any) => {
      const formatRoomExclusion: any = [];
      category?.ranges?.forEach((time: any) => {
        formatRoomExclusion.push(`${category.category}(${time.dateDep} to ${time.dateRet})`);
      });
      return formatRoomExclusion.join(', ');
    });
  };

  const handleFormatManualRoom = (item: object) => {
    const listRoom: any = [];
    item?.categories?.forEach((category: any) => {
      item?.ranges?.forEach((time: any) => {
        listRoom.push(`${category}(${time.dateDep} to ${time.dateRet})`);
      });
    });
    return listRoom;
  };

  const formatManualRoomExclusionData = (listRoom: object[]) => {
    return listRoom?.map((item: object) => {
      return handleFormatManualRoom(item).join(', ');
    });
  };

  const formatRoomExport = (data: any) => {
    const formatRoomDefault = data?.categories ? formatRoomExclusionData(data?.categories) : [];
    const formatRoomManual = data?.manualCategories ? formatManualRoomExclusionData(data?.manualCategories) : [];
    const formatData = [...formatRoomDefault, ...formatRoomManual];
    return formatData.join('|');
  };

  const formatDataExport = (listData: any, columns: any) => {
    return listData?.map((item: any) => {
      const data: any = {};
      columns.forEach((column: any) => {
        data[column.header] = item[column.field];
      });
      return data;
    });
  };

  const formatColumnExport = (columns: any, currentLanguage: string) => {
    const newField: any = [];
    const columnsTemp = columns.filter((column: any) => !['action', 'deleteStatus', 'notes'].includes(column.field));
    columnsTemp.forEach((column: any) => {
      if (column.fieldOther) {
        if (currentLanguage === LANG_ENGLISH) {
          newField.push({ field: column.field, header: `${column.header} (EN)` });
          newField.push({ field: column.fieldOther, header: `${column.header} (FR)` });
        } else {
          newField.push({ field: column.fieldOther, header: `${column.header} (EN)` });
          newField.push({ field: column.field, header: `${column.header} (FR)` });
        }
      } else {
        newField.push({ field: column.field, header: column.header });
      }
    });
    return newField;
  };

  const brochureListOption = [AFR, ASI, CRU, EUR, MID, NAM, SOM, SOP, SUN];

  const MANAGEMENT_TYPES: any = {
    HOTEL_MANAGEMENT,
    PACKAGE_MANAGEMENT,
    AIR_MANAGEMENT,
    TOUR_MANAGEMENT,
    CRUISE_MANAGEMENT,
    TERM_AND_CONDITIONS,
    PROMOS,
    QUOTE_CONFIGURATION,
    ADMIN_SETUP,
    CONTENT_MANAGEMENT_UPDATE,
    AIRLINE_MANAGEMENT,
    GROUP_TYPE_MANAGEMENT,
    ROLE,
    EMAIL_CONFIGURATION,
    EXPIRY_CONFIGURATION,
    MAX_ROOM_CONFIGURATION,
    OPEN_JAW_CONFIGURATION,
    CALENDAR_CONFIGURATION,
    MARGIN_SAFETY,
    RULE_ASSIGNMENT,
    FTV_CONFIGURATION
  };

  const HOTEL_TYPES: any = {
    PACKAGE_CONFIGURATION,
    HOTEL_CONFIGURATION
  };

  const PACKAGE_TYPES: any = {
    GATEWAY_MANAGEMENT,
    DESTINATION_MANAGEMENT,
    FLIGHT_RULE_MANAGEMENT,
    DESTINATION_GROUP_MANAGEMENT,
    GATE_DEST_DURATION_MANAGEMENT
  };

  const TOUR_TYPES: any = {
    TOUR_COMBINATION,
    TOUR_DETAIL,
    CITY
  };

  const CRUISE_TYPES: any = {
    CRUISE_DESTINATION,
    CRUISE_LINES,
    CRUISE_SHIPS,
    CRUISE_SAILINGS
  };

  const TERM_AND_COND_TYPES: any = {
    CUSTOMER_QUOTE_FORM,
    PDF,
    QUOTE_TO_BOOK_TERM
  };

  const AIRLINE_TYPE: any = {
    LOGO,
    AIRLINE_TAX
  };

  const LABELS: any = {
    [PACKAGE_CONFIGURATION]: i18n.global.t('management.hotel.package_configuration'),
    [HOTEL_CONFIGURATION]: i18n.global.t('management.hotel.hotel_configuration'),
    [GATEWAY_MANAGEMENT]: i18n.global.t('management.package.gateways'),
    [DESTINATION_MANAGEMENT]: i18n.global.t('management.package.destinations'),
    [FLIGHT_RULE_MANAGEMENT]: i18n.global.t('management.package.flight_instaquote_rules'),
    [DESTINATION_GROUP_MANAGEMENT]: i18n.global.t('management.package.destination_groupings'),
    [GATE_DEST_DURATION_MANAGEMENT]: i18n.global.t('permisson.gateway_destination_duration'),
    [HOTEL_MANAGEMENT]: i18n.global.t('role.hotel_management'),
    [PACKAGE_MANAGEMENT]: i18n.global.t('role.package_management'),
    [AIR_MANAGEMENT]: i18n.global.t('role.air_management'),
    [TOUR_MANAGEMENT]: i18n.global.t('role.tour_management'),
    [CRUISE_MANAGEMENT]: i18n.global.t('role.cruise_management'),
    [TERM_AND_CONDITIONS]: i18n.global.t('management.term_and_condition'),
    [TOUR_COMBINATION]: i18n.global.t('management.tour.tour_gateway_destination'),
    [TOUR_DETAIL]: i18n.global.t('management.tour.tour_details'),
    [CITY]: i18n.global.t('management.gate_des_region_refer'),
    [CRUISE_DESTINATION]: i18n.global.t('management.cruise.destinations'),
    [CRUISE_LINES]: i18n.global.t('management.cruise.cruise_lines'),
    [CRUISE_SHIPS]: i18n.global.t('management.cruise.cruise_ships'),
    [CRUISE_SAILINGS]: i18n.global.t('management.cruise.sailings'),
    [CUSTOMER_QUOTE_FORM]: i18n.global.t('common.customer_quote_form'),
    [PDF]: i18n.global.t('common.pdf'),
    [QUOTE_TO_BOOK_TERM]: i18n.global.t('common.booking_form'),
    [PROMOS]: i18n.global.t('menu.promos'),
    [QUOTE_CONFIGURATION]: i18n.global.t('menu.quote_configuration'),
    [ADMIN_SETUP]: i18n.global.t('menu.admin_setup'),
    [CONTENT_MANAGEMENT_UPDATE]: i18n.global.t('management.content_management_update'),
    [DATA_SYNC]: i18n.global.t('management.data_synchronization'),
    [AIRLINE_MANAGEMENT]: i18n.global.t('menu.airline_management'),
    [LOGO]: i18n.global.t('management.airline.logo_management'),
    [AIRLINE_TAX]: i18n.global.t('management.airline.airline_tax_management'),
    [GROUP_TYPE_MANAGEMENT]: i18n.global.t('common.group_type_management'),
    [ROLE]: i18n.global.t('menu.role_management'),
    [EMAIL_CONFIGURATION]: i18n.global.t('management.email.email_configuration'),
    [EXPIRY_CONFIGURATION]: i18n.global.t('quote_config.expiry_configuration'),
    [MAX_ROOM_CONFIGURATION]: i18n.global.t('quote_config.min_max_config'),
    [OPEN_JAW_CONFIGURATION]: i18n.global.t('quote_config.one_way_open_jaw_configuration'),
    [CALENDAR_CONFIGURATION]: i18n.global.t('quote_config.calendar_configuration'),
    [MARGIN_SAFETY]: i18n.global.t('quote_config.margin_safety'),
    [RULE_ASSIGNMENT]: i18n.global.t('quote_config.rule_assignment'),
    [FTV_CONFIGURATION]: i18n.global.t('quote_config.ftv_configuration'),
    [OPTION_CONFIGURATION]: i18n.global.t('quote_config.option_configuration')
  };

  const FILTER_OPTION: any = {
    [PACKAGE_CONFIGURATION]: [
      { key: 'ttsId', label: i18n.global.t('common.ttsId') },
      { key: 'chainName', label: i18n.global.t('management.hotel.chain_name') }
    ],
    [HOTEL_CONFIGURATION]: [
      { key: 'ttsId', label: i18n.global.t('common.ttsId') },
      { key: 'chainName', label: i18n.global.t('management.hotel.chain_name') }
    ],
    [AIR_MANAGEMENT]: [
      { key: 'airportCode', label: i18n.global.t('management.air.airport_code') },
      { key: 'cityName', label: i18n.global.t('management.air.city_name') }
    ],
    [GATEWAY_MANAGEMENT]: [
      { key: 'airportCode', label: i18n.global.t('management.air.airport_code') },
      { key: 'gatewayName', label: i18n.global.t('management.package.gateway_name') }
    ],
    [DESTINATION_MANAGEMENT]: [
      { key: 'airportCode', label: i18n.global.t('management.air.airport_code') },
      { key: 'destinationName', label: i18n.global.t('management.package.destination_name') }
    ],
    [FLIGHT_RULE_MANAGEMENT]: [
      { key: 'gatewayCode', label: i18n.global.t('management.package.gateway_code') },
      { key: 'destinationCode', label: i18n.global.t('management.package.destination_code') }
    ],
    [DESTINATION_GROUP_MANAGEMENT]: [{ key: 'airportCode', label: i18n.global.t('management.air.airport_code') }],
    [GATE_DEST_DURATION_MANAGEMENT]: [
      { key: 'gatewayCode', label: i18n.global.t('management.package.gateway_code') },
      { key: 'destinationAirportCode', label: i18n.global.t('report.destination_airport_code') }
    ],
    [TOUR_COMBINATION]: [
      { key: 'gatewayCode', label: i18n.global.t('management.package.gateway_code') },
      { key: 'destinationCode', label: i18n.global.t('management.package.destination_code') },
      { key: 'tourName', label: i18n.global.t('management.tour.tour_name') }
    ],
    [TOUR_DETAIL]: [
      { key: 'ttsId', label: i18n.global.t('common.ttsId') },
      { key: 'destinationCode', label: i18n.global.t('management.package.destination_code') }
    ],
    [CITY]: [
      { key: 'airportCode', label: i18n.global.t('management.air.airport_code') },
      { key: 'cityName', label: i18n.global.t('management.air.city_name') }
    ],
    [CRUISE_DESTINATION]: [{ key: 'destinationCode', label: i18n.global.t('common.destination') }],
    [CRUISE_LINES]: [{ key: 'cruiseLineName', label: i18n.global.t('management.cruise.cruise_line_name') }],
    [CRUISE_SHIPS]: [{ key: 'cruiseShipName', label: i18n.global.t('management.cruise.cruise_ship_name') }],
    [CRUISE_SAILINGS]: [
      { key: 'cruiseLineName', label: i18n.global.t('management.cruise.cruise_line_name') },
      { key: 'cruiseShipName', label: i18n.global.t('management.cruise.cruise_ship_name') }
    ]
  };

  const createRuleHistoryItem = (type: string, subTypes: any = []) => {
    return {
      type,
      label: LABELS[type] || '',
      value: subTypes.map((subType: any) => ({
        type: subType,
        label: LABELS[subType] || '',
        filterOption: FILTER_OPTION[subType] || []
      }))
    };
  };

  const ruleHistoryList = [
    createRuleHistoryItem(MANAGEMENT_TYPES.HOTEL_MANAGEMENT, [HOTEL_TYPES.PACKAGE_CONFIGURATION, HOTEL_TYPES.HOTEL_CONFIGURATION]),
    createRuleHistoryItem(MANAGEMENT_TYPES.PACKAGE_MANAGEMENT, [
      PACKAGE_TYPES.GATEWAY_MANAGEMENT,
      PACKAGE_TYPES.DESTINATION_MANAGEMENT,
      PACKAGE_TYPES.FLIGHT_RULE_MANAGEMENT,
      PACKAGE_TYPES.DESTINATION_GROUP_MANAGEMENT,
      PACKAGE_TYPES.GATE_DEST_DURATION_MANAGEMENT
    ]),
    createRuleHistoryItem(MANAGEMENT_TYPES.AIR_MANAGEMENT, [MANAGEMENT_TYPES.AIR_MANAGEMENT]),
    createRuleHistoryItem(MANAGEMENT_TYPES.TOUR_MANAGEMENT, [TOUR_TYPES.TOUR_COMBINATION, TOUR_TYPES.TOUR_DETAIL, TOUR_TYPES.CITY]),
    createRuleHistoryItem(MANAGEMENT_TYPES.CRUISE_MANAGEMENT, [CRUISE_TYPES.CRUISE_DESTINATION, CRUISE_TYPES.CRUISE_LINES, CRUISE_TYPES.CRUISE_SHIPS, CRUISE_TYPES.CRUISE_SAILINGS]),
    createRuleHistoryItem(MANAGEMENT_TYPES.TERM_AND_CONDITIONS, [TERM_AND_COND_TYPES.CUSTOMER_QUOTE_FORM, TERM_AND_COND_TYPES.PDF, TERM_AND_COND_TYPES.QUOTE_TO_BOOK_TERM]),
    createRuleHistoryItem(MANAGEMENT_TYPES.PROMOS, [MANAGEMENT_TYPES.PROMOS]),
    createRuleHistoryItem(MANAGEMENT_TYPES.QUOTE_CONFIGURATION, [
      MANAGEMENT_TYPES.EMAIL_CONFIGURATION,
      EXPIRY_CONFIGURATION,
      MAX_ROOM_CONFIGURATION,
      OPEN_JAW_CONFIGURATION,
      CALENDAR_CONFIGURATION,
      OPTION_CONFIGURATION,
      MARGIN_SAFETY,
      RULE_ASSIGNMENT,
      FTV_CONFIGURATION
    ]),
    createRuleHistoryItem(MANAGEMENT_TYPES.ADMIN_SETUP, [MANAGEMENT_TYPES.ADMIN_SETUP]),
    createRuleHistoryItem(MANAGEMENT_TYPES.CONTENT_MANAGEMENT_UPDATE, [DATA_SYNC]),
    createRuleHistoryItem(MANAGEMENT_TYPES.AIRLINE_MANAGEMENT, [AIRLINE_TYPE.LOGO, AIRLINE_TYPE.AIRLINE_TAX]),
    createRuleHistoryItem(MANAGEMENT_TYPES.GROUP_TYPE_MANAGEMENT, [MANAGEMENT_TYPES.GROUP_TYPE_MANAGEMENT]),
    createRuleHistoryItem(MANAGEMENT_TYPES.ROLE, [MANAGEMENT_TYPES.ROLE])
  ];

  const flightRuleType: any = {
    FULL_RISK: 'FULL RISK',
    SHARED_RISK: 'SHARED RISK',
    FBR: 'FBR',
    PRE_REG: 'PRE REG'
  };

  const flightRuleTypeList = [flightRuleType.FULL_RISK, flightRuleType.SHARED_RISK, flightRuleType.FBR, flightRuleType.PRE_REG];

  const quoteTypeList = [
    {
      label: i18n.global.t('advance_quote.form.package'),
      value: PACKAGE
    },
    {
      label: i18n.global.t('advance_quote.form.hotel'),
      value: HOTEL_ONLY
    },
    {
      label: i18n.global.t('advance_quote.form.air'),
      value: AIR_ONLY
    },
    {
      label: i18n.global.t('advance_quote.form.cruise'),
      value: CRUISE
    },
    {
      label: i18n.global.t('common.labels.tour'),
      value: TOUR
    },
    {
      label: i18n.global.t('common.incentives'),
      value: INCENTIVES
    }
  ];

  const promosTypeList = [
    {
      label: i18n.global.t('promos.home_page'),
      value: HOME_PAGE
    },
    {
      label: i18n.global.t('promos.top_banner'),
      value: TOP_BANNER
    },
    {
      label: i18n.global.t('promos.side_promos'),
      value: SIDE_PROMOS
    },
    {
      label: i18n.global.t('promos.promo_boxes'),
      value: PROMO_BOX
    },
    {
      label: i18n.global.t('promos.form_promos'),
      value: FORM_PROMOS
    },
    {
      label: i18n.global.t('promos.surface_promo'),
      value: SURFACE_PROMO
    },
    {
      label: i18n.global.t('promos.accordion_promos'),
      value: ACCORDION_PROMOS
    },
    {
      label: i18n.global.t('promos.promo_section_title'),
      value: PROMO_TITLE_CONFIG
    }
  ];

  const roleTabs = [
    {
      label: i18n.global.t('role.group_travel_home'),
      value: TRAVEL_HOME
    },
    {
      label: i18n.global.t('role.my_quote'),
      value: MY_QUOTE
    },
    {
      label: i18n.global.t('role.quote_management'),
      value: QUOTE_MANAGEMENT
    },
    {
      label: i18n.global.t('role.administration'),
      value: ADMINISTRATION
    },
    {
      label: i18n.global.t('role.report'),
      value: REPORT
    }
  ];

  const adminSetupTabs = [
    {
      label: i18n.global.t('gqt_admin_setup.manage_urls'),
      value: URL
    },
    {
      label: i18n.global.t('gqt_admin_setup.email_url'),
      value: EMAIL
    },
    {
      label: i18n.global.t('gqt_admin_setup.agency_blacklist'),
      value: AGENCY_BLACKLIST
    }
  ];

  const flightTabs = [
    {
      label: i18n.global.t('quote_config.full_risk'),
      value: FULL_RISK
    },
    {
      label: i18n.global.t('quote_config.share_risk'),
      value: SHARED_RISK
    },
    {
      label: i18n.global.t('quote_config.fbr'),
      value: FBR
    },
    {
      label: i18n.global.t('quote_config.pre_reg'),
      value: PRE_REG
    }
  ];

  const emailNoticeConfigTabs = [
    {
      label: i18n.global.t('management.email.deposit_due'),
      value: EMAIL_DEPOSIT_DUE
    },
    {
      label: i18n.global.t('management.email.final_payment_due'),
      value: EMAIL_FINAL_PAYMENT_DUE
    },
    {
      label: i18n.global.t('management.email.passenger_list_due'),
      value: EMAIL_PASSENGER_LIST_DUE
    },
    {
      label: i18n.global.t('management.email.quote_expiring'),
      value: EMAIL_QUOTE_EXPIRING
    }
  ];

  const configTabs = [
    {
      label: i18n.global.t('config_items.packages'),
      value: PACKAGE
    },
    {
      label: i18n.global.t('config_items.hotel'),
      value: HOTEL
    },
    {
      label: i18n.global.t('config_items.air'),
      value: AIR
    },
    {
      label: i18n.global.t('config_items.cruises'),
      value: CRUISE
    },
    {
      label: i18n.global.t('config_items.tour'),
      value: MDT
    }
  ];

  const configMinMax = [
    {
      label: i18n.global.t('quote_config.min_group_size'),
      value: GROUP_SIZE
    },
    {
      label: i18n.global.t('quote_config.max_room'),
      value: MAX_ROOM
    }
  ];

  const quoteTypeListValue = [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR, INCENTIVES];
  const handleCheckQuoteType = (type: string) => {
    if (type === PDF || type === QUOTE_TO_BOOK_TERM) {
      return [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR, INCENTIVES];
    } else {
      return [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR];
    }
  };

  const renderQuoteType = (typeQuote: string) => {
    return quoteTypeList.find((item: any) => item.value === typeQuote)?.label;
  };

  const convertTimeZero = (listDate: any, type?: string) => {
    if (listDate?.length) {
      return listDate?.map((date: any) => {
        return type === 'excludeCategory'
          ? {
              dateDep: resetTimer(date.dateDep),
              dateRet: resetTimer(date.dateRet)
            }
          : {
              start: resetTimer(date.start),
              end: resetTimer(date.end)
            };
      });
    } else {
      return [];
    }
  };

  const multiDataRender = (type?: string) => {
    const dataMap: any = {
      [PROMOS]: {
        label: i18n.global.t('common.labels.promos_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_promos_type'),
        key: 'promosType',
        listOption: promosTypeList,
        initFilter: promosTypeList.map((item: any) => item.value)
      },
      [EMAIL_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [EMAIL_CONFIGURATION]
      },
      [EXPIRY_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [EXPIRY_CONFIGURATION]
      },
      [MAX_ROOM_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [MAX_ROOM_CONFIGURATION]
      },
      [OPEN_JAW_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [OPEN_JAW_CONFIGURATION]
      },
      [CALENDAR_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [CALENDAR_CONFIGURATION]
      },
      [FTV_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [FTV_CONFIGURATION]
      },
      [RULE_ASSIGNMENT]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [RULE_ASSIGNMENT]
      },
      [ADMIN_SETUP]: {
        label: i18n.global.t('gqt_admin_setup.gqt_admin_setup_type'),
        placeHolderLabel: i18n.global.t('worksheet.select_type'),
        key: 'configurableItemType',
        listOption: adminSetupTabs,
        initFilter: adminSetupTabs.map((item: any) => item.value)
      },
      [ROLE]: {
        label: i18n.global.t('common.labels.role_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_role_type'),
        key: 'roleType',
        listOption: roleTabs,
        initFilter: roleTabs.map((item: any) => item.value)
      },
      [MARGIN_SAFETY]: {
        label: i18n.global.t('quote_config.flight_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_flight_type'),
        key: 'configurableItemType',
        initFilter: [MARGIN_SAFETY]
      },
      [OPTION_CONFIGURATION]: {
        label: i18n.global.t('common.labels.quote_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        initFilter: [OPTION_CONFIGURATION]
      }
    };
    return dataMap[type!] ?? null;
  };

  const multiDataConfigItemRender = (type?: string) => {
    const dataMap: any = {
      [EMAIL_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        listOption: emailNoticeConfigTabs,
        initFilter: emailNoticeConfigTabs.map((item: any) => item.value)
      },
      [EXPIRY_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        listOption: configTabs,
        initFilter: configTabs.map((item: any) => item.value)
      },
      [CALENDAR_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        listOption: configTabs,
        initFilter: configTabs.map((item: any) => item)
      },
      [MARGIN_SAFETY]: {
        label: i18n.global.t('quote_config.flight_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_flight_type'),
        key: 'configurableItemType',
        listOption: flightTabs,
        initFilter: flightTabs.map((item: any) => item.value)
      },
      [ADMIN_SETUP]: {
        label: i18n.global.t('gqt_admin_setup.gqt_admin_setup_type'),
        placeHolderLabel: i18n.global.t('worksheet.select_type'),
        key: 'configurableItemType',
        listOption: adminSetupTabs,
        initFilter: adminSetupTabs.map((item: any) => item.value)
      },
      [OPTION_CONFIGURATION]: {
        label: i18n.global.t('common.labels.quote_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_quote_type'),
        key: 'configurableItemType',
        listOption: configTabs,
        initFilter: configTabs.map((item: any) => item.value)
      },
      [MAX_ROOM_CONFIGURATION]: {
        label: i18n.global.t('common.labels.configurable_item_type'),
        placeHolderLabel: i18n.global.t('common.labels.select_configurable_item_type'),
        key: 'configurableItemType',
        listOption: configMinMax,
        initFilter: configMinMax.map((item: any) => item.value)
      }
    };
    return dataMap[type!] ?? null;
  };

  const checkCalendarConfig = (type: any) => {
    const typesCheck = CALENDAR_CONFIGURATION;
    return typesCheck.includes(type);
  };

  const airFiltersInit = (globalFliter: any) => {
    return {
      global: { value: globalFliter, matchMode: FilterMatchMode.CONTAINS },
      airportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      airportName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      airportNameFR: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      cityName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      cityNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      region: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      regionFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      countryName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      countryNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      destSts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      gateSts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      destSVId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    };
  };

  return {
    quoteSts,
    priorityStatus,
    statusPriorityNumber,
    departments,
    renderLocalTimeFromUtc,
    renderAmount,
    renderDateTimeOption,
    getDepartment,
    renderAmountAccordingRouter,
    removeHtmlTags,
    apiOrder,
    createUnassignedStatus,
    renderTitleBooking,
    styleColumnExport,
    renderNameFileExport,
    formatRoomExport,
    formatDataExport,
    formatColumnExport,
    brochureListOption,
    ruleHistoryList,
    createRuleHistoryItem,
    flightRuleTypeList,
    quoteTypeList,
    quoteTypeListValue,
    renderQuoteType,
    handleCheckQuoteType,
    convertTimeZero,
    multiDataRender,
    multiDataConfigItemRender,
    checkCalendarConfig,
    airFiltersInit
  };
}

export function useModalComposable() {
  const dialog = useDialog();

  const handleOpenErrorMessage = (data: any, isCenter: boolean = true) => {
    const dialogRef = dialog.open(ErrorMessageModal, {
      props: {
        style: {
          'max-width': '45vw',
          'min-width': '25vw'
        },
        modal: true,
        closable: false
      },
      data: {
        description: data,
        isAlignCenter: isCenter
      },
      emits: {
        onClose: () => {
          dialogRef.close();
        }
      }
    });
  };

  const showMinNotification = (message: string) => {
    const dialogRef = dialog.open(ValidateQuoteFormNotification, {
      props: {
        header: i18n.global.t('advance_quote.form.notice'),
        modal: true,
        style: {
          maxWidth: '90vw'
        }
      },
      data: {
        message: message
      },
      templates: {
        footer: () => {
          return [
            h(
              'div',
              { class: 'text-center' },
              h(Button, {
                label: i18n.global.t('advance_quote.form.return_to_form'),
                autofocus: true,
                onClick: () => dialogRef.close()
              })
            )
          ];
        }
      }
    });
  };

  return { handleOpenErrorMessage, showMinNotification };
}
